import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import { clsx } from 'clsx';
import { TrendingUp } from 'lucide-react';

const transactionSchema = z.object({
  description: z.string().min(2, 'Descrição deve ter no mínimo 2 caracteres'),
  amount: z.number().positive('Valor deve ser maior que zero'),
  type: z.enum(['income', 'expense']),
  date: z.string().min(1, 'Data é obrigatória'),
  accountId: z.string().min(1, 'Conta é obrigatória'),
  categoryId: z.string().min(1, 'Categoria é obrigatória'),
  notes: z.string().optional(),
  remindInvest: z.boolean().default(false),
});

const TransactionForm = ({ transaction, onSubmit, onCancel, isLoading }) => {
  const { accounts } = useAccounts();
  const { incomeCategories, expenseCategories } = useCategories();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      description: '',
      amount: '',
      type: 'expense',
      date: new Date().toISOString().split('T')[0],
      accountId: '',
      categoryId: '',
      notes: '',
      remindInvest: false,
    },
  });

  const selectedType = watch('type');
  const categories = selectedType === 'income' ? incomeCategories : expenseCategories;

  useEffect(() => {
    if (transaction) {
      reset({
        description: transaction.description || '',
        amount: transaction.amount ? Math.abs(transaction.amount) : '',
        type: transaction.type || 'expense',
        date: transaction.date || new Date().toISOString().split('T')[0],
        accountId: transaction.accountId || '',
        categoryId: transaction.categoryId || '',
        notes: transaction.notes || '',
        remindInvest: transaction.remindInvest || false,
      });
    }
  }, [transaction, reset]);

  useEffect(() => {
    const currentCategoryId = watch('categoryId');
    if (currentCategoryId) {
      const categoryExists = categories.find(c => c.categoryId === currentCategoryId);
      if (!categoryExists) {
        setValue('categoryId', '');
      }
    }
  }, [selectedType, categories, setValue, watch]);

  const accountOptions = accounts.map(acc => ({
    value: acc.accountId,
    label: `${acc.icon} ${acc.name}`,
  }));

  const categoryOptions = categories.map(cat => ({
    value: cat.categoryId,
    label: `${cat.icon} ${cat.name}`,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Tipo de transação */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setValue('type', 'expense')}
            className={clsx(
              'py-3 px-4 rounded-lg border-2 font-medium transition-all',
              selectedType === 'expense'
                ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
            )}
          >
            💸 Despesa
          </button>
          <button
            type="button"
            onClick={() => setValue('type', 'income')}
            className={clsx(
              'py-3 px-4 rounded-lg border-2 font-medium transition-all',
              selectedType === 'income'
                ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
            )}
          >
            💰 Receita
          </button>
        </div>
      </div>

      <Input
        label="Descrição"
        placeholder="Ex: Supermercado, Salário, etc"
        error={errors.description?.message}
        {...register('description')}
      />

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Valor"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? '')}
              onBlur={field.onBlur}
              error={errors.amount?.message}
            />
          )}
        />

        <DatePicker
          label="Data"
          error={errors.date?.message}
          {...register('date')}
        />
      </div>

      <Select
        label="Conta"
        options={accountOptions}
        placeholder="Selecione uma conta"
        error={errors.accountId?.message}
        {...register('accountId')}
      />

      <Select
        label="Categoria"
        options={categoryOptions}
        placeholder="Selecione uma categoria"
        error={errors.categoryId?.message}
        {...register('categoryId')}
      />

      <Input
        label="Observações (opcional)"
        placeholder="Adicione uma nota..."
        error={errors.notes?.message}
        {...register('notes')}
      />

      {selectedType === 'income' && (
        <label className={clsx(
          'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all',
          watch('remindInvest')
            ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
        )}>
          <input
            type="checkbox"
            className="hidden"
            {...register('remindInvest')}
          />
          <div className={clsx(
            'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
            watch('remindInvest')
              ? 'bg-emerald-100 dark:bg-emerald-900/40'
              : 'bg-gray-100 dark:bg-gray-700'
          )}>
            <TrendingUp size={18} className={watch('remindInvest') ? 'text-emerald-600' : 'text-gray-400'} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Lembrar de investir ao receber
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              O MyBank vai calcular quanto sobra apos o orcamento e te avisar na hora
            </p>
          </div>
        </label>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {transaction?.transactionId ? 'Salvar' : 'Criar Transação'}
        </Button>
      </div>
    </form>
  );
};

export default TransactionForm;
